import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useTheme } from '@mui/material/styles';
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { db, doc, onSnapshot } from "../../firebaseConfig";

function Sidenav({ color, brand, brandName, routes, profileInfo, team, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [teamLogoUrl, setTeamLogoUrl] = useState("");
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const toggleSidenav = () => {
    setMiniSidenav(dispatch, !miniSidenav);
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileOpen]);

  useEffect(() => {
    let unsubscribeLogo;
    if (profileInfo?.teamId) {
      const logoDocRef = doc(db, "teamLogos", profileInfo.teamId);
      unsubscribeLogo = onSnapshot(
        logoDocRef,
        (logoDoc) => {
          if (logoDoc.exists()) {
            const logoUrl = logoDoc.data().logoUrl || "";
            setTeamLogoUrl(logoUrl);
          } else {
            setTeamLogoUrl("");
          }
        },
        (error) => {
          console.error("Sidenav: Error listening to team logo:", error);
          setTeamLogoUrl("");
        }
      );
    } else {
      setTeamLogoUrl("");
    }

    return () => {
      if (unsubscribeLogo) {
        unsubscribeLogo();
      }
    };
  }, [profileInfo?.teamId]);

  useEffect(() => {
    function handleMiniSidenav() {
      const isSmallScreen = window.innerWidth < 1200;
      // Only apply transparent/white sidenav settings for larger screens
      if (!isSmallScreen) {
        setTransparentSidenav(dispatch, transparentSidenav);
        setWhiteSidenav(dispatch, whiteSidenav);
      }
      // Close mobile menu on resize to larger screens
      if (!isSmallScreen && isMobileOpen) {
        setIsMobileOpen(false);
      }
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, isMobileOpen, transparentSidenav, whiteSidenav]);

  const renderRoutes = routes.map(
    ({ type, name, icon, title, noCollapse, key, href, route }) => {
      let returnValue;

      if (type === "collapse") {
        returnValue = href ? (
          <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
            <SidenavCollapse name={name} icon={icon} active={key === location.pathname.replace("/", "")} noCollapse={noCollapse} />
          </Link>
        ) : (
          <NavLink key={key} to={route} onClick={() => setIsMobileOpen(false)}>
            <SidenavCollapse name={name} icon={icon} active={key === location.pathname.replace("/", "")} />
          </NavLink>
        );
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }
      return returnValue;
    }
  );

  return (
    <>
      <SidenavRoot
        {...rest}
        variant="permanent"
        ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
        sx={(theme) => ({
          width: {
            xs: isMobileOpen ? "220px" : "0px",
            xl: miniSidenav ? "120px" : "220px",
          },
          height: "100vh",
          overflowX: "hidden",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          left: 0,
          top: 0,
          transform: {
            xs: isMobileOpen ? "translateX(0)" : "translateX(-100%)",
            xl: "none",
          },
          transition: theme.transitions.create(["width", "transform"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          zIndex: theme.zIndex.drawer,
          backgroundColor: transparentSidenav
            ? "transparent"
            : whiteSidenav
            ? theme.palette.background.paper
            : theme.palette.background.default,
          pointerEvents: "auto",
        })}
      >
        <MDBox pt={3} pb={1} px={4} textAlign="center" sx={{ flexShrink: 0, position: "relative" }}>
          {/* Mobile Close Button */}
          <MDBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={toggleMobileMenu}
            sx={{ cursor: "pointer" }}
          >
            <MDTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold", color: iconColor }}>
                <CloseIcon />
              </Icon>
            </MDTypography>
          </MDBox>
          <MDBox component={NavLink} to="/" display="flex" alignItems="center">
            {brand && (
              <MDBox component="img" src={brand} alt="Brand" width="4rem" borderRadius="20%" />
            )}
            <MDBox
              width={!brandName && "100%"}
              sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
            >
              <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
                {brandName}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />

        {profileInfo && profileInfo.fullName && profileInfo.profilePicURL && (
          <>
            <MDBox p={2} display="flex" alignItems="center" flexDirection="column" sx={{ flexShrink: 0 }}>
              <MDBox
                component="img"
                src={profileInfo.profilePicURL}
                alt={profileInfo.fullName}
                width="4rem"
                height="4rem"
                borderRadius="50%"
                mb={1}
              />
              <MDTypography
                variant="button"
                fontWeight="medium"
                color={textColor}
                sx={{
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  overflow: "visible",
                  textOverflow: "unset",
                  textAlign: "center",
                }}
              >
                {profileInfo.fullName}
              </MDTypography>
              {teamLogoUrl && profileInfo.teamId && (
                <>
                  <Divider
                    light={
                      (!darkMode && !whiteSidenav && !transparentSidenav) ||
                      (darkMode && !transparentSidenav && whiteSidenav)
                    }
                    sx={{ width: "100%", my: 1 }}
                  />
                  <MDBox mt={1} display="flex" flexDirection="column" alignItems="center">
                    <MDBox
                      component="img"
                      src={teamLogoUrl}
                      alt="Team Logo"
                      width="3rem"
                      height="3rem"
                      borderRadius="20%"
                    />
                    {team && team.name && (
                      <MDTypography
                        variant="caption"
                        fontWeight="regular"
                        color={textColor}
                        sx={{ mt: 0.5, maxWidth: "100%", textAlign: "center" }}
                      >
                        {team.name}
                      </MDTypography>
                    )}
                  </MDBox>
                </>
              )}
            </MDBox>
            <Divider
              light={
                (!darkMode && !whiteSidenav && !transparentSidenav) ||
                (darkMode && !transparentSidenav && whiteSidenav)
              }
            />
          </>
        )}

        <List
          sx={{
            flex: 1,
            overflowY: "auto",
            maxHeight: "calc(100vh - 13rem)",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
              borderRadius: "4px",
              "&:hover": {
                background: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
              },
            },
            scrollbarWidth: "thin",
            scrollbarColor: darkMode
              ? "rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1)"
              : "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1)",
          }}
        >
          {renderRoutes}
        </List>
      </SidenavRoot>

      {/* Desktop Toggle Button */}
      <MDBox
        display={{ xs: "none", xl: "block" }}
        position="fixed"
        top={8}
        left={miniSidenav ? 120 : 220}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          pointerEvents: "none",
        }}
      >
        <IconButton
          onClick={toggleSidenav}
          sx={{
            p: 1,
            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
            "&:hover": {
              backgroundColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
            },
            pointerEvents: "auto",
          }}
        >
          {miniSidenav ? (
            <ChevronRightIcon fontSize="medium" sx={{ color: iconColor }} />
          ) : (
            <ChevronLeftIcon fontSize="medium" sx={{ color: iconColor }} />
          )}
        </IconButton>
      </MDBox>

      {/* Mobile Hamburger Button */}
      <MDBox
        display={{ xs: "block", xl: "none" }}
        position="fixed"
        top={8}
        left={8}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      >
        <IconButton
          onClick={toggleMobileMenu}
          sx={{
            p: 1,
            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
            "&:hover": {
              backgroundColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <MenuIcon fontSize="medium" sx={{ color: iconColor }} />
        </IconButton>
      </MDBox>

      {/* Mobile Backdrop */}
      <MDBox
        sx={{
          display: { xs: "block", xl: "none" },
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: (theme) => theme.zIndex.drawer - 1,
          transition: "opacity 0.3s ease-in-out",
          opacity: isMobileOpen ? 1 : 0,
          pointerEvents: isMobileOpen ? "auto" : "none",
        }}
        onClick={toggleMobileMenu}
      />
    </>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  profileInfo: PropTypes.shape({
    fullName: PropTypes.string,
    profilePicURL: PropTypes.string,
    teamId: PropTypes.string,
    teamRole: PropTypes.string,
  }),
  team: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
};

export default Sidenav;